import { LoadingButton } from '@mui/lab'
import {
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material'
import TablePagination from '@mui/material/TablePagination'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { InboundTripPAPSDto, VehicleDto } from 'dtos'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { DEBOUNCE_DELAY, Junction } from 'localConstants'
import { VehiclesParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { formatAddress, formatCurrency, useDebounce } from 'utils'

interface ManageVehiclesAddVehiclesDialogProps {
  isLoading: boolean
  inboundTripPAPS: InboundTripPAPSDto
  open: boolean
  onClose: () => void
  onSave: (values: any) => void
}

export default function ManageVehiclesAddVehiclesDialog({ isLoading, inboundTripPAPS, onClose, onSave, open }: ManageVehiclesAddVehiclesDialogProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false
  })

  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicles, setVehicles] = useState<VehicleDto[]>([])
  const [parameters, setParameters] = useState<VehiclesParameters>({
    page: 0,
    pageSize: 0,
    order: 'asc',
    orderBy: ''
  })

  const getVehicles = useDebounce((parameters: VehiclesParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers
      .getVehicles(parameters)
      .then(response => {
        setVehicles(response.value)
        setTotalCount(response.totalCount ?? 0)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (open && inboundTripPAPS) {
      setParameters({
        page: 0,
        pageSize: 10,
        order: 'asc',
        orderBy: 'vin',
        inboundTripPAPSGuid: inboundTripPAPS.inboundTripPAPSGuid,
        statuses: ['isActive', 'isLocked', 'hasNoInboundTripPAPS'],
        junction: Junction.AND
      })
    }
  }, [open, inboundTripPAPS])

  useEffect(() => {
    getVehicles(parameters)
  }, [getVehicles, parameters])

  return (
    <Dialog maxWidth='md' open={open}>
      <Formik
        enableReinitialize
        initialValues={[] as VehicleDto[]}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
      >
        {({ handleSubmit, setValues, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='flex-start'>
                  <DialogTitle>Add Vehicles to PAPS</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='PAPS #' value={inboundTripPAPS.papsNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Exporter Company' value={inboundTripPAPS.exporterCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Buyer Company' value={inboundTripPAPS.buyerCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Vehicle Destination Company' value={inboundTripPAPS.vehicleDestinationCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Vehicle Destination Company Address'
                      value={inboundTripPAPS.vehicleDestinationCompanyAddress ? formatAddress(inboundTripPAPS.vehicleDestinationCompanyAddress) : ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField label='Title Destination Company' value={inboundTripPAPS.titleDestinationCompany?.name} />
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField
                      label='Title Destination Company Address'
                      value={inboundTripPAPS.titleDestinationCompanyAddress ? formatAddress(inboundTripPAPS.titleDestinationCompanyAddress) : ''}
                    />
                  </Grid>

                  {loadingState.isGettingVehicles && (
                    <Grid item container alignItems='center' justifyContent='center' sx={{ my: 1 }}>
                      <Grid>
                        <CircularProgress color='primary' size={50} />
                      </Grid>
                    </Grid>
                  )}

                  {!loadingState.isGettingVehicles && (
                    <Grid item xs={12}>
                      <Card variant='outlined' sx={{ width: '100%' }}>
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell padding='checkbox'>
                                  <Checkbox
                                    color='primary'
                                    checked={values.length === totalCount && values.length !== 0}
                                    disabled={isLoading || values.length === 0}
                                    indeterminate={values.length > 0 && values.length !== totalCount}
                                    onChange={() => {
                                      if (values.length === totalCount) {
                                        setValues([])
                                      } else {
                                        setValues(vehicles)
                                      }
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'vin' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'vin'}
                                    direction={parameters.orderBy === 'vin' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'vin' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'vin'
                                      })
                                    }}
                                  >
                                    VIN
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'year' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'year'}
                                    direction={parameters.orderBy === 'year' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'year' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'year'
                                      })
                                    }}
                                  >
                                    Year
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'make' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'make'}
                                    direction={parameters.orderBy === 'make' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'make' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'make'
                                      })
                                    }}
                                  >
                                    Make
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'model' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'model'}
                                    direction={parameters.orderBy === 'model' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'model' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'model'
                                      })
                                    }}
                                  >
                                    Model
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell
                                  sx={theme => ({ color: theme.palette.primary.main })}
                                  sortDirection={parameters.orderBy === 'amountUSD' ? parameters.order : false}
                                >
                                  <TableSortLabel
                                    active={parameters.orderBy === 'amountUSD'}
                                    direction={parameters.orderBy === 'amountUSD' ? parameters.order : undefined}
                                    onClick={() => {
                                      setParameters({
                                        ...parameters,
                                        order: parameters.orderBy === 'amountUSD' && parameters.order === 'asc' ? 'desc' : 'asc',
                                        orderBy: 'amountUSD'
                                      })
                                    }}
                                  >
                                    Amount (USD)
                                  </TableSortLabel>
                                </TableCell>

                                <TableCell></TableCell>
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {vehicles?.map((vehicle, index) => (
                                <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                                  <TableCell padding='checkbox'>
                                    <Checkbox
                                      color='primary'
                                      checked={values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)}
                                      disabled={isLoading}
                                      onChange={() => {
                                        setValues(prev =>
                                          values.some(sv => sv.vehicleGuid === vehicle.vehicleGuid)
                                            ? prev.filter(sv => sv.vehicleGuid !== vehicle.vehicleGuid)
                                            : [...prev, vehicle]
                                        )
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell>{vehicle.vin}</TableCell>

                                  <TableCell>{vehicle.year}</TableCell>

                                  <TableCell>{vehicle.make}</TableCell>

                                  <TableCell>{vehicle.model}</TableCell>

                                  <TableCell>{vehicle.amountUSD ? formatCurrency(vehicle.amountUSD) : ''}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>

                            <TableFooter>
                              <TableRow>
                                <TablePagination
                                  rowsPerPageOptions={[5, 10, 25]}
                                  SelectProps={{ fullWidth: false }}
                                  count={totalCount}
                                  rowsPerPage={parameters.pageSize}
                                  page={parameters.page}
                                  onPageChange={(_, page) => {
                                    setParameters({
                                      ...parameters,
                                      page
                                    })
                                  }}
                                  onRowsPerPageChange={e => {
                                    setParameters({
                                      ...parameters,
                                      page: 0,
                                      pageSize: e.target.value as unknown as number
                                    })
                                  }}
                                />
                              </TableRow>
                            </TableFooter>
                          </Table>
                        </TableContainer>
                      </Card>
                    </Grid>
                  )}

                  <Grid item>
                    <ExplanationAccordion variant='outlined'>
                      || The list of VINs displayed are the VINs in VTS2 that have been locked, are not already associated with any PAPS, and have matching
                      criteria as displayed in the dialog header. Vehicles are displayed with these fields: checkbox for selecting vehicles, VIN, Year, Make,
                      Model, and Amount (USD). Note that if a PAPS has an Exporter Company but no Buyer Company, only vehicles with that selected Exporter
                      Company and no Buyer Company may be added to the PAPS. || Users will select the checkbox in the rows of the vehicles they wish to add to
                      the PAPS and then press the “SAVE” button which will add the selected VINs to the PAPS and close this dialog. Users can also press
                      “CANCEL” to close this dialog without adding any vehicles to the PAPS. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton loading={isLoading} type='submit' variant='contained'>
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
